import { useEffect, useMemo, useState } from 'react';
import {
  cancelQuote,
  cancelSettlement,
  depositReceived,
  feeLines,
  renderTerms,
  type CancellationFeeLine,
  type CancellationReason,
  type IsoDate,
  type ReservationService,
  type ReservationView,
  type RoomStayView,
  type StayTermsBase,
} from '@lemontaigu/pms-core';
import { fmtDate, fmtEuros, parseEurosToCents } from './logic.js';

// OB3 (docs/PMS-OWNED-BOOKINGS-BRIEF.md §4) — « Annuler » and « No-show » on a
// booking the PMS owns, in place of a window.confirm:
//
//   cancel   the reason (required, the closed list of 0138), per logement its
//            terms as sold and the fee they retain TODAY, the deposit received,
//            and what the desk keeps — prefilled with the fee, editable down to 0
//            (« Renoncer aux frais »). The cancel event records the fee (Q3).
//   no_show  the same panel, showing the no-show amount under the terms.
//            Information only: the no-show invoice is ARRHES A5's.
//
// The money goes the way it does today: the desk keys the VAD and records the
// payment, or refunds. Nothing here posts.

export type CancelDialogAction = 'cancel' | 'no_show';

export interface CancelChoice {
  reasonCode: string | null;
  reason: string | null;
  fee: CancellationFeeLine[] | null;
}

export function CancelDialog({
  action,
  booking,
  stays,
  paidCents,
  today,
  reservations,
  onConfirm,
  onClose,
}: {
  action: CancelDialogAction;
  booking: ReservationView;
  /** The logements the act covers: every one of the booking for a cancellation,
   *  the one room for a no-show on a multi-room booking. */
  stays: readonly RoomStayView[];
  /** What the payment read reports as paid (0 for a booking the PMS made). */
  paidCents: number;
  today: IsoDate;
  reservations: Pick<ReservationService, 'cancellationReasons' | 'termsBase'>;
  /** Runs the act; resolves to an error message, or null when done. */
  onConfirm: (choice: CancelChoice) => Promise<string | null>;
  onClose: () => void;
}) {
  const [reasons, setReasons] = useState<CancellationReason[] | null>(null);
  const [bases, setBases] = useState<StayTermsBase[] | null>(null);
  const [loadErr, setLoadErr] = useState('');
  const [reasonCode, setReasonCode] = useState('');
  const [reason, setReason] = useState('');
  const [keptText, setKeptText] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    let gone = false;
    void (async () => {
      const [r, b] = await Promise.all([
        action === 'cancel' ? reservations.cancellationReasons() : Promise.resolve(null),
        reservations.termsBase(booking.id),
      ]);
      if (gone) return;
      if (r !== null) {
        if (r.ok) setReasons(r.value);
        else setLoadErr(r.error.message);
      }
      if (b.ok) setBases(b.value);
      else setLoadErr(b.error.message);
    })();
    return () => {
      gone = true;
    };
  }, [action, booking.id, reservations]);

  const quote = useMemo(() => (bases === null ? null : cancelQuote(stays, bases, today)), [stays, bases, today]);
  const feeCents = quote?.reduce((s, q) => s + q.feeCents, 0) ?? 0;
  const noShowCents = quote?.reduce((s, q) => s + q.noShowCents, 0) ?? 0;
  const received = depositReceived(booking.guarantee, booking.vadAmountCents, paidCents);
  const keptCents = keptText === null ? feeCents : parseEurosToCents(keptText);
  const keptOk = keptCents !== null && keptCents >= 0 && keptCents <= feeCents;
  const owed = action === 'cancel' ? (keptOk ? keptCents : feeCents) : noShowCents;
  const settle = cancelSettlement(owed, received);
  const who = `${(booking.guest?.lastName ?? '?').toUpperCase()} ${booking.guest?.firstName ?? ''}`.trim();
  const title = action === 'cancel' ? 'Annuler la réservation' : 'No-show';

  const ready =
    quote !== null && !busy && (action === 'no_show' || (reasons !== null && reasonCode !== '' && keptOk));

  const confirm = async () => {
    if (!ready || quote === null) return;
    setBusy(true);
    setErr('');
    const e = await onConfirm(
      action === 'cancel'
        ? {
            reasonCode,
            reason: reason.trim() === '' ? null : reason.trim(),
            fee: feeLines(quote, keptCents ?? 0),
          }
        : { reasonCode: null, reason: null, fee: null },
    );
    setBusy(false);
    if (e !== null) setErr(e);
  };

  return (
    <div className="pms-overlay" onClick={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="pms-overlay-panel is-narrow" role="dialog" aria-modal="true" aria-label={title}>
        <div className="pms-form pms-cancel">
          <h3>
            {title} — {who}
          </h3>
          <p className="pms-sub">
            {fmtDate(booking.checkinDate)} → {fmtDate(booking.checkoutDate)} · calcul au {fmtDate(today)}
          </p>

          {loadErr && <p className="auth-err">Erreur : {loadErr}</p>}

          {action === 'cancel' && (
            <label className="pms-cancel-reason">
              Motif (obligatoire)
              <select value={reasonCode} onChange={(e) => setReasonCode(e.target.value)} disabled={reasons === null}>
                <option value="">— choisir —</option>
                {(reasons ?? []).map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.labelFr}
                  </option>
                ))}
              </select>
            </label>
          )}
          {action === 'cancel' && (
            <label className="pms-cancel-reason">
              Précision (facultatif)
              <input value={reason} onChange={(e) => setReason(e.target.value)} />
            </label>
          )}

          {quote === null ? (
            <p className="pms-sub">Lecture des conditions…</p>
          ) : (
            <table className="pms-table pms-cancel-terms">
              <thead>
                <tr>
                  <th>Logement</th>
                  <th>Conditions vendues</th>
                  <th className="pms-num">Base</th>
                  <th className="pms-num">{action === 'cancel' ? 'Frais ce jour' : 'No-show'}</th>
                </tr>
              </thead>
              <tbody>
                {quote.map((q, i) => {
                  const stay = stays.find((s) => s.id === q.roomStayId)!;
                  const text =
                    q.terms === null ? null : renderTerms(q.terms, 'fr', { arrival: stay.checkinDate, totalCents: q.baseCents });
                  return (
                    <tr key={q.roomStayId}>
                      <td>
                        {quote.length > 1 ? `Logement ${i + 1}` : 'Séjour'}
                        {stay.roomNumber ? ` · ch. ${stay.roomNumber}` : ''}
                      </td>
                      <td>
                        {text === null ? (
                          <span className="pms-sub">Non enregistrées : aucuns frais calculés</span>
                        ) : (
                          <>
                            <strong>{q.terms?.labelFr ?? q.terms?.code ?? 'Conditions'}</strong>
                            {q.terms?.relaxed === true ? ' (assouplies)' : ''}
                            <div className="pms-sub">
                              {action === 'cancel' ? text.cancellation.join(' · ') : text.noShow}
                            </div>
                          </>
                        )}
                      </td>
                      <td className="pms-num">{fmtEuros(q.baseCents)}</td>
                      <td className="pms-num">{fmtEuros(action === 'cancel' ? q.feeCents : q.noShowCents)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          <dl className="pms-cancel-sum">
            {action === 'cancel' && (
              <>
                <dt>Frais selon les conditions</dt>
                <dd>{fmtEuros(feeCents)}</dd>
                <dt>
                  <label htmlFor="pms-cancel-kept">Montant retenu</label>
                </dt>
                <dd>
                  <input
                    id="pms-cancel-kept"
                    className="pms-num"
                    inputMode="decimal"
                    value={keptText ?? (feeCents / 100).toFixed(2).replace('.', ',')}
                    onChange={(e) => setKeptText(e.target.value)}
                  />{' '}
                  <button type="button" className="pms-mini" onClick={() => setKeptText('0')} disabled={feeCents === 0}>
                    Renoncer aux frais
                  </button>
                  {!keptOk && <div className="auth-err">Entre 0 et {fmtEuros(feeCents)}.</div>}
                </dd>
              </>
            )}
            {action === 'no_show' && (
              <>
                <dt>No-show selon les conditions</dt>
                <dd>
                  {fmtEuros(noShowCents)}
                  <div className="pms-sub">Pour information : la facture de no-show viendra avec la clôture (A5).</div>
                </dd>
              </>
            )}
            <dt>Arrhes reçues</dt>
            <dd>{fmtEuros(received)}</dd>
            <dt>{settle.refundCents > 0 ? 'À rembourser' : 'À prélever (VAD)'}</dt>
            <dd>
              <strong>{fmtEuros(settle.refundCents > 0 ? settle.refundCents : settle.chargeCents)}</strong>
            </dd>
          </dl>

          {err && <p className="auth-err">Erreur : {err}</p>}

          <div className="pms-form-actions">
            <button type="button" className="auth-btn" onClick={onClose} disabled={busy}>
              Retour
            </button>
            <button type="button" className="auth-btn" onClick={() => void confirm()} disabled={!ready}>
              {action === 'cancel' ? 'Confirmer l’annulation' : 'Confirmer le no-show'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
